import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";
import colorSet from "@/src/styles/color";
import zIndex from "@/src/styles/zIndex";
import Icon from "@/src/components/atom/Icon";
import CloseSvg from "@/src/assets/icons/icon-close-black.svg";
import DefaultUserSvg from "@/src/assets/icons/icon-default-user.svg";
import { useSendbirdStateContext } from "@sendbird/uikit-react";
import { useUploadSmallImageMutation } from "@/src/store/apis/media";
import Loader from "@/src/components/atom/Loader";
import { useTranslation } from "react-i18next";

const SENDBIRD_DEFAULT_PROFILE_URL =
  "https://static.sendbird.com/sample/profiles/profile_placeholder.png";

interface UserMetaData {
  contact?: string;
  email?: string;
  profileName?: string;
}

interface EditProfileModalProps {
  visible: boolean;
  onClose: () => void;
  user: {
    userId: string;
    nickname: string;
    profileUrl?: string;
    metaData?: UserMetaData;
  } | null;
  onSave: (updates: {
    nickname: string;
    profileImage?: string;
  }) => Promise<void>;
}

const EditProfileModal: React.FC<EditProfileModalProps> = ({
  visible,
  onClose,
  user,
  onSave,
}) => {
  const { t } = useTranslation("chat");
  const [nickname, setNickname] = useState("");
  const [profileImage, setProfileImage] = useState("");
  const [tempImageFile, setTempImageFile] = useState<File | null>(null);
  const [tempImagePreview, setTempImagePreview] = useState<string | null>(null);
  const [isDeleted, setIsDeleted] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const globalStore = useSendbirdStateContext();
  const sb = globalStore?.stores?.sdkStore?.sdk;
  const [uploadSmallImage] = useUploadSmallImageMutation();

  useEffect(() => {
    if (user) {
      setNickname(user.nickname || "");

      const currentProfileUrl = user.profileUrl;
      if (
        currentProfileUrl &&
        currentProfileUrl !== SENDBIRD_DEFAULT_PROFILE_URL
      ) {
        setProfileImage(currentProfileUrl);
        setIsDeleted(false);
      } else {
        setProfileImage("");
        setIsDeleted(true);
      }

      setTempImageFile(null);
      setTempImagePreview(null);
    }
  }, [user]);

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        alert(t("fileSizeTooLarge"));
        return;
      }

      if (!file.type.startsWith("image/")) {
        alert(t("pleaseUploadImage"));
        return;
      }

      setTempImageFile(file);
      setIsDeleted(false);
      const reader = new FileReader();
      reader.onloadend = () => {
        setTempImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDeleteImage = () => {
    setTempImageFile(null);
    setTempImagePreview(null);
    setIsDeleted(true);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleSave = async () => {
    setIsUploading(true);
    try {
      if (!sb) {
        throw new Error("SendBird SDK not initialized");
      }

      let finalImageUrl = profileImage;

      if (tempImageFile) {
        try {
          // SendBird SDK를 사용하여 프로필 이미지 직접 업로드
          const params = {
            nickname: nickname,
            profileImage: tempImageFile,
          };

          const updatedUser = await sb.updateCurrentUserInfo(params);
          finalImageUrl = updatedUser.profileUrl;
        } catch (uploadError: any) {
          try {
            const result = await uploadSmallImage({
              files: [tempImageFile],
              folder: "commons/",
            }).unwrap();

            if (result && result.length > 0) {
              finalImageUrl = result[0].mediaUrl;

              // 서버 업로드 성공 후 프로필 URL 업데이트
              const updateParams = {
                nickname: nickname,
                profileUrl: finalImageUrl,
              };
              await sb.updateCurrentUserInfo(updateParams);
            }
          } catch (serverError) {
            console.error("Fallback server upload also failed:", serverError);
            alert(`Failed to upload image. Please try again.`);
            return;
          }
        }
      } else if (isDeleted) {
        // 이미지 삭제 시 닉네임만 업데이트 (빈 URL로)
        const params = {
          nickname: nickname,
          profileUrl: "",
        };
        await sb.updateCurrentUserInfo(params);
        finalImageUrl = "";
      } else {
        // 닉네임만 변경된 경우
        const params = {
          nickname: nickname,
        };
        await sb.updateCurrentUserInfo(params);
      }

      await onSave({
        nickname,
        profileImage: finalImageUrl || "",
      });
      onClose();
    } catch (error) {
      console.error("Failed to save profile:", error);
      alert(t("failedToSaveProfile"));
    } finally {
      setIsUploading(false);
    }
  };

  if (!visible || !user) return null;
  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>{t("editProfile")}</ModalTitle>
          <CloseButton onClick={onClose}>
            <Icon iconSrc={CloseSvg} iconSize={22} />
          </CloseButton>
        </ModalHeader>

        <ModalBody>
          <ProfileImageSection>
            <SectionLabel>{t("profileImage")}</SectionLabel>
            <ProfileImageContainer>
              <ProfileImageWrapper>
                {tempImagePreview || (!isDeleted && profileImage) ? (
                  <ProfileImage
                    src={tempImagePreview || profileImage}
                    alt="Profile"
                  />
                ) : (
                  <DefaultProfileWrapper>
                    <Icon iconSrc={DefaultUserSvg} iconSize={45.71} />
                  </DefaultProfileWrapper>
                )}
              </ProfileImageWrapper>
              <ProfileButtonGroup>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleImageSelect}
                  style={{ display: "none" }}
                  id="profile-image-upload"
                />
                {tempImagePreview || (!isDeleted && profileImage) ? (
                  <>
                    <ProfileButton as="label" htmlFor="profile-image-upload">
                      {t("edit")}
                    </ProfileButton>
                    <DeleteButton onClick={handleDeleteImage}>
                      {t("delete")}
                    </DeleteButton>
                  </>
                ) : (
                  <ProfileButton as="label" htmlFor="profile-image-upload">
                    {t("upload")}
                  </ProfileButton>
                )}
              </ProfileButtonGroup>
            </ProfileImageContainer>
          </ProfileImageSection>

          <FormSection>
            <FormField>
              <Label>{t("profileNameCompany")}</Label>
              <ReadOnlyInput
                type="text"
                value={
                  user?.metaData?.profileName || nickname || "Name (Company)"
                }
                readOnly
                disabled
              />
            </FormField>

            <FormField>
              <Label>{t("email")}</Label>
              <ReadOnlyInput
                type="email"
                value={user?.metaData?.email || "-"}
                readOnly
                disabled
              />
            </FormField>

            <FormField>
              <Label>{t("contact")}</Label>
              <ReadOnlyInput
                type="tel"
                value={user?.metaData?.contact || "-"}
                readOnly
                disabled
              />
            </FormField>
          </FormSection>
        </ModalBody>

        <ModalFooter>
          <CancelButton onClick={onClose}>{t("cancel")}</CancelButton>
          <SaveButton
            onClick={handleSave}
            disabled={isUploading}
            $isLoading={isUploading}
          >
            {isUploading ? (
              <Loader
                size={16}
                tickColor="white"
                trackColor="rgba(255, 255, 255, 0.3)"
              />
            ) : (
              t("save")
            )}
          </SaveButton>
        </ModalFooter>
      </ModalContent>
    </ModalOverlay>
  );
};

export default EditProfileModal;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: ${zIndex.floatingChatEditModal};
`;

const ModalContent = styled.div`
  background: ${colorSet.white};
  border-radius: 16px;
  width: 480px;
  box-shadow: 0px 4px 24px 0px rgba(0, 0, 0, 0.1);
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 24px 16px 24px;
  gap: 16px;
`;

const ModalTitle = styled.h2`
  font-family: "Pretendard", sans-serif;
  font-weight: 700;
  font-size: 20px;
  line-height: 30px;
  color: ${colorSet.gray2};
  margin: 0;
  flex: 1;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 4px;
  transition: background-color 0.2s;

  &:hover {
    background-color: #f1f4f6;
  }

  &:active {
    background-color: #e7edf0;
  }
`;

const ModalBody = styled.div`
  padding: 0 24px 8px 24px;
`;

const ProfileImageSection = styled.div`
  margin-bottom: 34px;
`;

const SectionLabel = styled.label`
  font-family: "Roboto", sans-serif;
  font-weight: 400;
  font-size: 12px;
  line-height: 12px;
  color: ${colorSet.gray2};
`;

const ProfileImageContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-top: 16px;
`;

const ProfileImageWrapper = styled.div`
  position: relative;
`;

const ProfileImage = styled.img`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  object-fit: cover;
`;

const DefaultProfileWrapper = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background-color: ${colorSet.gray7};
`;

const ProfileButtonGroup = styled.div`
  display: flex;
  gap: 8px;
`;

const ProfileButton = styled.button<{ disabled?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 6px 16px;
  border: 1px solid ${colorSet.gray7};
  border-radius: 4px;
  background: white;
  color: ${colorSet.gray2};
  font-family: "Pretendard", sans-serif;
  font-weight: 600;
  font-size: 13px;
  line-height: 18px;
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  transition:
    color 0.2s,
    border-color 0.2s;
  min-width: 74px;
  height: 30px;
  opacity: ${(props) => (props.disabled ? 0.6 : 1)};

  &:hover {
    color: #166dd7;
    border-color: #166dd7;
  }

  &:active {
    color: #166dd7;
    border-color: #166dd7;
    background-color: #d7e7f9;
  }
`;

const DeleteButton = styled(ProfileButton)`
  color: ${colorSet.red1};
  border-color: ${colorSet.red6};

  &:hover {
    color: ${colorSet.red2};
    border-color: ${colorSet.red4};
  }

  &:active {
    color: ${colorSet.red1};
    border-color: ${colorSet.red1};
    background-color: ${colorSet.red7};
  }
`;

const FormSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
`;

const FormField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 16px 0;
`;

const Label = styled.label`
  font-family: "Pretendard", sans-serif;
  font-weight: 700;
  font-size: 13px;
  line-height: 18px;
  color: ${colorSet.gray6};
`;

const ReadOnlyInput = styled.input`
  border: none;
  outline: none;
  font-family: "Pretendard", sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 18px;
  color: ${colorSet.gray2};
  padding: 0;
  background: transparent;
  cursor: not-allowed;
`;

const ModalFooter = styled.div`
  display: flex;
  gap: 8px;
  padding: 34px 32px 24px 32px;
  justify-content: flex-end;
`;

const CancelButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px 16px;
  border: 1px solid ${colorSet.gray7};
  border-radius: 4px;
  background: white;
  color: ${colorSet.gray2};
  font-family: "Pretendard", sans-serif;
  font-weight: 600;
  font-size: 13px;
  line-height: 18px;
  cursor: pointer;
  transition:
    color 0.2s,
    border-color 0.2s,
    background-color 0.2s;
  min-width: 74px;
  height: 38px;

  &:hover {
    color: #166dd7;
    border-color: #166dd7;
  }

  &:active {
    color: #166dd7;
    border-color: #166dd7;
    background-color: #d7e7f9;
  }
`;

const SaveButton = styled.button<{ disabled?: boolean; $isLoading?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px 16px;
  border: none;
  border-radius: 4px;
  background: ${(props) =>
    props.$isLoading ? "#166DD7" : props.disabled ? colorSet.gray4 : "#166DD7"};
  color: white;
  font-family: "Pretendard", sans-serif;
  font-weight: 600;
  font-size: 13px;
  line-height: 18px;
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  transition: background-color 0.2s;
  min-width: 74px;
  height: 38px;

  &:hover {
    background: ${(props) =>
      props.$isLoading
        ? "#166DD7"
        : props.disabled
          ? colorSet.gray4
          : "#1761C2"};
  }

  &:active {
    background: ${(props) =>
      props.$isLoading
        ? "#166DD7"
        : props.disabled
          ? colorSet.gray4
          : "#1150AA"};
  }
`;
